import type { AnchorHTMLAttributes, ReactNode } from 'react';

export type LinkButtonVariant =
  | 'primary'
  | 'red'
  | 'dark'
  | 'outline'
  | 'outline-light'
  | 'light';

export type LinkButtonSize = 'sm' | 'md' | 'lg';

interface LinkButtonProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: string;
  variant?: LinkButtonVariant;
  size?: LinkButtonSize;
  /** Tailwind에서 ring 색을 바꾸고 싶을 때 등 */
  className?: string;
  children: ReactNode;
}

const base =
  'inline-flex items-center justify-center gap-1.5 rounded-full font-semibold transition-all duration-150 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';

const sized: Record<LinkButtonSize, string> = {
  sm: 'px-4 py-2 text-[13px]',
  md: 'px-[18px] py-2.5 text-[14px]',
  lg: 'px-[26px] py-3.5 text-[15px]',
};

const skin: Record<LinkButtonVariant, string> = {
  primary: 'bg-brand-500 text-white hover:bg-brand-600 focus-visible:ring-brand-500',
  red: 'bg-[#FA0F00] text-white hover:bg-[#d80d00] focus-visible:ring-[#FA0F00]',
  dark: 'bg-neutral-950 text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-950 dark:hover:bg-neutral-200 focus-visible:ring-neutral-950',
  outline:
    'border border-black/15 dark:border-white/15 text-neutral-950 dark:text-neutral-100 hover:border-neutral-950 hover:bg-neutral-950 hover:text-white dark:hover:bg-white dark:hover:text-neutral-950 focus-visible:ring-neutral-950',
  'outline-light':
    'border border-white/30 text-white hover:bg-white/10 focus-visible:ring-white',
  light:
    'bg-white dark:bg-dark-card text-neutral-950 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-white/10 focus-visible:ring-white',
};

/**
 * LinkButton 의 스킨(클래스) 계산만 분리한 프레임워크 무관 헬퍼.
 * react-router 등 다른 앵커/링크 요소에 동일한 시각 언어를 입힐 때 재사용한다.
 * (ui 패키지는 라우팅 의존을 금지하므로, 라우터용 래퍼는 앱 쪽에서 이 함수를 쓴다.)
 */
export function linkButtonClass({
  variant = 'dark',
  size = 'md',
  className = '',
}: {
  variant?: LinkButtonVariant;
  size?: LinkButtonSize;
  className?: string;
} = {}): string {
  return `${base} ${sized[size]} ${skin[variant]} ${className}`.trim();
}

/**
 * 앵커 형태 버튼.
 * - 페이지 라우팅용 CTA에 사용 (홈, 상품 페이지의 CTA 등)
 * - react-router-dom의 <Link>가 필요하면 외부에서 `as={Link}` 패턴 대신
 *   호출자가 직접 <Link>를 쓰고 LinkButton 클래스만 가져다 쓸 수도 있지만,
 *   95% 케이스가 anchor라서 굳이 추상화하지 않음.
 * - 외부 링크면 target="_blank" + rel을 호출자가 명시.
 */
export function LinkButton({
  href,
  variant = 'dark',
  size = 'md',
  className = '',
  children,
  target,
  rel,
  ...rest
}: LinkButtonProps) {
  const computedRel =
    target === '_blank' ? (rel ?? 'noopener noreferrer') : rel;

  return (
    <a
      href={href}
      target={target}
      rel={computedRel}
      className={linkButtonClass({ variant, size, className })}
      {...rest}
    >
      {children}
    </a>
  );
}
